import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import type { Vote, VotesData } from '../../types';
import { changeVoteLocal } from '../local-data';
import { unvoteQuery } from './unvote.query';
import type { VoteResponse } from './vote.query';
import { voteQuery } from './vote.query';

export function useChangeVoteMutation(queryKey: QueryKey) {
  const { widgetToken, answers } = useSettings();
  const queryClient = useQueryClient();

  return useMutation<
    VoteResponse['payload'],
    string,
    { voteId: Vote['id']; unvoteId: Vote['id']; pid: string },
    { previousVotes?: VotesData }
  >({
    mutationFn: async ({ voteId, pid }) => {
      await unvoteQuery(pid, widgetToken);
      return await voteQuery(voteId, widgetToken);
    },
    onMutate: async ({ unvoteId, voteId }) => {
      await queryClient.cancelQueries({
        queryKey
      });

      const previousVotes = queryClient.getQueryData<VotesData>(queryKey);

      if (previousVotes) {
        const newVotes = changeVoteLocal({
          unvoteId,
          voteId,
          answers,
          data: previousVotes
        });

        queryClient.setQueryData(queryKey, newVotes);
      }

      return { previousVotes };
    },
    onError: (_, __, context) => {
      if (context && context.previousVotes) {
        queryClient.setQueryData(queryKey, context.previousVotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
}
